import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for managing real-time SSE updates
 * Falls back to slower polling if SSE fails
 */
export const useRealtimeUpdates = ({
  isAuthenticated,
  onFriendLocation,
  onPublicLocation,
  onReport,
  onAlert,
  onFriendRequest,
  onConnected,
  onDisconnected,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const eventSourceRef = useRef(null);
  const streamTokenRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const isConnectingRef = useRef(false); // Lock to prevent multiple connection attempts
  const maxReconnectAttempts = 5;

  // Store callbacks in refs to avoid dependency issues
  const callbacksRef = useRef({
    onFriendLocation,
    onPublicLocation,
    onReport,
    onAlert,
    onFriendRequest,
    onConnected,
    onDisconnected,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onFriendLocation,
      onPublicLocation,
      onReport,
      onAlert,
      onFriendRequest,
      onConnected,
      onDisconnected,
    };
  }, [onFriendLocation, onPublicLocation, onReport, onAlert, onFriendRequest, onConnected, onDisconnected]);

  // Get stream token from API
  const getStreamToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('boat360_token');
      if (!token) return null;

      const response = await fetch(`${BACKEND_URL}/api/stream/token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get stream token');
      }

      const data = await response.json();
      return data.stream_token;
    } catch (error) {
      console.error('Failed to get stream token:', error);
      return null;
    }
  }, []);

  // Update subscription (bbox, layers)
  const updateSubscription = useCallback(async (bbox, layers = {}) => {
    try {
      const token = localStorage.getItem('boat360_token');
      if (!token) return;

      await fetch(`${BACKEND_URL}/api/stream/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bbox,
          show_public: layers.showPublic ?? true,
          show_friends: layers.showFriends ?? true,
          show_reports: layers.showReports ?? true,
          show_alerts: layers.showAlerts ?? true,
        }),
      });
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  }, []);

  // Connect to SSE stream
  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      return;
    }
    
    // Already connected
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      return;
    }

    isConnectingRef.current = true;
    setConnectionStatus('connecting');

    // Get stream token
    const streamToken = await getStreamToken();
    if (!streamToken) {
      setConnectionStatus('error');
      isConnectingRef.current = false;
      return;
    }

    streamTokenRef.current = streamToken;

    try {
      // Create EventSource connection
      const url = `${BACKEND_URL}/api/stream/updates?token=${streamToken}`;
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('SSE connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
        isConnectingRef.current = false;
        callbacksRef.current.onConnected?.();
      };

      eventSource.onmessage = (event) => {
        try {
          // Skip heartbeat/keepalive messages
          if (event.data.startsWith(':') || event.data.trim() === '') return;

          const parsed = JSON.parse(event.data);
          const { type, data } = parsed;

          switch (type) {
            case 'connected':
              console.log('SSE stream established for user:', data.user_id);
              break;
            case 'friend_location':
              callbacksRef.current.onFriendLocation?.(data);
              break;
            case 'public_location':
              callbacksRef.current.onPublicLocation?.(data);
              break;
            case 'report':
              callbacksRef.current.onReport?.(data);
              break;
            case 'alert':
              callbacksRef.current.onAlert?.(data);
              // Show toast for alerts
              if (data.alert_type === 'SOS') {
                toast.error(`🆘 SOS from ${data.from_display_name}!`, {
                  duration: 10000,
                });
              } else {
                toast.info(`${data.from_display_name} says: I'm OK`, {
                  duration: 5000,
                });
              }
              break;
            case 'friend_request':
              callbacksRef.current.onFriendRequest?.(data);
              toast.info(`New friend request from ${data.from_display_name}`);
              break;
            default:
              console.log('Unknown SSE event type:', type);
          }
        } catch (error) {
          // Ignore parse errors for keepalive messages
          if (!event.data.startsWith(':')) {
            console.error('Error parsing SSE event:', error);
          }
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE error:', error);
        setIsConnected(false);
        setConnectionStatus('error');
        callbacksRef.current.onDisconnected?.();

        // Close and cleanup
        eventSource.close();
        eventSourceRef.current = null;
        isConnectingRef.current = false;

        // Attempt reconnection with backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          console.log(`SSE reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          console.log('SSE max reconnection attempts reached, falling back to polling');
          setConnectionStatus('fallback');
        }
      };

    } catch (error) {
      console.error('Failed to establish SSE connection:', error);
      setConnectionStatus('error');
      isConnectingRef.current = false;
    }
  }, [getStreamToken]);

  // Disconnect from SSE stream
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    isConnectingRef.current = false;
    setIsConnected(false);
    setConnectionStatus('disconnected');
    callbacksRef.current.onDisconnected?.();
  }, []);

  // Connect when authenticated - use isAuthenticated as only dependency
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated]); // Only depend on isAuthenticated

  return {
    isConnected,
    connectionStatus,
    updateSubscription,
    reconnect: connect,
    disconnect,
  };
};

export default useRealtimeUpdates;
